import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { findNavLabel } from './nav-items';
import { useAuthStore } from '@/auth/auth-store';

const truncate = (s: string) => (s.length > 12 ? `${s.slice(0, 12)}…` : s);

/** Segmentos de acción con label humano fijo. */
const ACTION_LABELS: Record<string, string> = { new: 'Crear' };

/**
 * #6 — breadcrumbs derivados del pathname: la sección (label del NAV) y,
 * si aplica, el label humano de la sub-página (kyc/strikes/prizes/proposals)
 * o el id del recurso truncado. No requiere tocar cada ruta.
 */
export function Breadcrumbs() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const segments = pathname.split('/').filter(Boolean);
  // A store owner's root is their store, not the team's Panel (2026-11-02).
  const isStoreOwner = useAuthStore((s) => s.user?.role === 'USER');
  const rootLabel = isStoreOwner ? 'Mi tienda' : 'Panel';
  const rootTo = isStoreOwner ? '/tienda' : '/';

  if (segments.length === 0) {
    return (
      <nav aria-label="Ruta de navegación" className="text-sm text-text-muted">
        {rootLabel}
      </nav>
    );
  }

  const sectionPath = `/${segments[0]}`;
  const sectionLabel = findNavLabel(sectionPath) ?? segments[0];
  const rest = segments.slice(1);

  return (
    <nav aria-label="Ruta de navegación" className="flex items-center gap-1 text-sm">
      <Link
        to={rootTo}
        className="text-text-muted hover:text-text-primary outline-none focus-visible:ring-1 focus-visible:ring-gold"
      >
        {rootLabel}
      </Link>
      {(rest.length === 0 || sectionPath !== rootTo) && (
        <ChevronRight size={14} className="text-text-muted" aria-hidden />
      )}
      {rest.length === 0 ? (
        <span className="text-text-primary">{sectionLabel}</span>
      ) : (
        <>
          {/* The store owner's root IS their home section: not twice in a row. */}
          {sectionPath !== rootTo && (
            <Link
              to={sectionPath}
              className="text-text-muted hover:text-text-primary outline-none focus-visible:ring-1 focus-visible:ring-gold"
            >
              {sectionLabel}
            </Link>
          )}
          {rest.map((seg, i) => {
            const humanLabel =
              i === 0 ? (findNavLabel(`${sectionPath}/${seg}`) ?? ACTION_LABELS[seg]) : undefined;
            return (
              <span key={`${seg}-${i}`} className="flex items-center gap-1">
                <ChevronRight size={14} className="text-text-muted" aria-hidden />
                {humanLabel ? (
                  <span className="text-text-primary">{humanLabel}</span>
                ) : (
                  <span className="font-mono text-xs text-text-primary">{truncate(seg)}</span>
                )}
              </span>
            );
          })}
        </>
      )}
    </nav>
  );
}
